import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { PageHeader } from '@/modules/common/components/PageHeader';
import { SearchableMultiSelect } from '@/modules/common/components/SearchableMultiSelect';
import { TableFilter } from '@/modules/common/components/TableFilter';
import { useLearningBudgetTransactionsDigest } from '@/modules/common/hooks/data-hooks/useLearningBudgetTransactionsDigest';
import { useLearningBudgetTransactionsExport } from '@/modules/common/hooks/data-hooks/useLearningBudgetTransactionsExport';
import { useLearningBudgetTransactionsWithDetail } from '@/modules/common/hooks/data-hooks/useLearningBudgetTransactionsWithDetail';
import { createDownload } from '@/modules/common/lib/download';
import { formatConstant } from '@/modules/common/lib/string';
import { useGetTeams } from '@/modules/employees/hooks/useTeams';
import { LearningBudgetTransactionsRouteQueryParams } from '@/routes/_admin/learning-budgets/transactions';
import {
  LearningBudgetTransactionLabel,
  LearningBudgetTransactionStatus,
} from '@/types/learning-budget';
import { Download, List, Search } from '@mui/icons-material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Button,
  Checkbox,
  Divider,
  ListItemText,
  MenuItem,
  Stack,
  Tab,
  TextField,
} from '@mui/material';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { PaginationState } from '@tanstack/react-table';
import { subDays, subMonths, subWeeks } from 'date-fns';
import debounce from 'lodash.debounce';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useRef, useState } from 'react';
import { DatePickerModal } from '../components/DatePickerModal/DatePickerModal';
import { LearningBudgetTransactionsDigest } from '../components/LearningBudgetTransactionsDigest/LearningBudgetTransactionsDigest';
import { ListLearningBudgetTransactionsTable } from '../components/LearningBudgetTransactionsTable';
import { CustomDateInterval } from '../types';

const statusFilterOptions = Object.values(LearningBudgetTransactionStatus).map(
  status => ({
    label: formatConstant(status),
    value: status,
  }),
);

export function TransactionsPage() {
  const { employer, isLoading: isLoadingUser } = useCurrentUserContext() ?? {};
  const navigate = useNavigate();
  const search = useSearch({
    strict: false,
  }) as LearningBudgetTransactionsRouteQueryParams;

  const [teamName, setTeamName] = useState<string>();
  const [datePickerModalOpen, setDatePickerModalOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const parsedDateIntervalPresetRanges = useMemo(() => {
    switch (search?.dateInterval) {
      case CustomDateInterval.Past24Hours:
        return {
          initialDate: subDays(new Date(), 1).toISOString(),
          finalDate: new Date().toISOString(),
        };
      case CustomDateInterval.LastWeek:
        return {
          initialDate: subWeeks(new Date(), 1).toISOString(),
          finalDate: new Date().toISOString(),
        };
      case CustomDateInterval.LastMonth:
        return {
          initialDate: subMonths(new Date(), 1).toISOString(),
          finalDate: new Date().toISOString(),
        };
      case CustomDateInterval.LastYear:
        return {
          initialDate: subDays(new Date(), 365).toISOString(),
          finalDate: new Date().toISOString(),
        };
      default:
        return {
          initialDate: search?.initialDate,
          finalDate: search?.finalDate,
        };
    }
  }, [search.dateInterval, search.initialDate, search.finalDate]);

  const baseQueryFilters = {
    company: employer?._id,
    team: search?.teamId,
    user: search?.userId,
  };

  const {
    isLoading: isLoadingTransactionsDigest,
    data: { data: transactionsDigest } = {},
  } = useLearningBudgetTransactionsDigest(baseQueryFilters, {
    enabled: !!employer?._id,
  });

  const { data: { data: filteredTeams } = {} } = useGetTeams({
    name: teamName || undefined,
  });

  const {
    data: { data: selectedTeams } = {},
    isLoading: isLoadingSelectedTeams,
  } = useGetTeams(
    { _id: search.teamId },
    {
      enabled: !!search.teamId,
    },
  );

  const {
    isLoading: isLoadingTransactions,
    data: { data: transactions, metadata: { total = 0, page = 1 } = {} } = {},
  } = useLearningBudgetTransactionsWithDetail({
    filters: {
      ...baseQueryFilters,
      status: Array.isArray(search.status) ? search.status : undefined,
      userName: search.userName || undefined,
      labels: Array.isArray(search.labels) ? search.labels : undefined,
      ...parsedDateIntervalPresetRanges,
    },
    pagination: {
      page: search?.page || 1,
      limit: search?.limit || 10,
    },
    options: {
      enabled: !!employer?._id,
    },
  });

  const {
    refetch: exportLearningBudgetTransactions,
    isLoading: isLoadingExport,
  } = useLearningBudgetTransactionsExport(
    {
      ...baseQueryFilters,
      status: Array.isArray(search.status) ? search.status : undefined,
      userName: search.userName || undefined,
      ...parsedDateIntervalPresetRanges,
    },
    { enabled: false },
  );

  const paginationState = {
    pageIndex: (page ?? 1) - 1 || 0,
    pageSize: search.limit || 10,
  };

  const showClearButton = Object.entries(search)
    .filter(([key]) => !['page', 'limit'].includes(key))
    .some(([, value]) => (Array.isArray(value) ? value.length > 0 : !!value));

  const onPaginationChange = ({ pageIndex, pageSize }: PaginationState) => {
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        page: pageIndex + 1,
        limit: pageSize,
      }),
    });
  };

  const availableTeams = useMemo(() => {
    return [...(filteredTeams ?? []), ...(selectedTeams ?? [])]
      .filter(
        (team, index, self) =>
          index === self.findIndex(t => t._id === team._id),
      )
      .filter(team => {
        if (search.teamId?.includes(team._id)) {
          return true;
        }
        return filteredTeams?.some(t => t._id === team._id);
      });
  }, [filteredTeams, selectedTeams, search.teamId]);

  const onDownloadClick = async () => {
    try {
      const { data: response } = await exportLearningBudgetTransactions();

      await createDownload(
        response,
        'learning-budget-transactions.csv',
        'text/csv',
      );
    } catch {
      enqueueSnackbar('Error downloading transactions', {
        variant: 'error',
      });
    }
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader pageTitle="Transactions" />
      <TabContext value="learning_budget_transactions">
        <TabList className="flex gap-5 px-8 border-b mt-8">
          <Tab
            label={
              <div className="flex gap-2 items-center">
                <List />
                Transactions
              </div>
            }
            value={'learning_budget_transactions'}
          />
        </TabList>
        <TabPanel
          value="learning_budget_transactions"
          className="px-0 flex flex-col h-full"
        >
          <LearningBudgetTransactionsDigest
            className="px-8"
            variant={
              search.userId ? 'individual' : search?.teamId ? 'team' : 'company'
            }
            isLoading={isLoadingTransactionsDigest || isLoadingUser}
            transactionsDigest={transactionsDigest}
          />
          <Divider className="mt-8" />
          <div className="px-8 pt-4 h-full">
            <ListLearningBudgetTransactionsTable
              tableOptions={{
                data: transactions ?? [],
                state: {
                  isLoading: isLoadingTransactions,
                  pagination: paginationState,
                },
                rowCount: total ?? 0,
                onPaginationChange: updater => {
                  return typeof updater === 'function'
                    ? onPaginationChange(updater(paginationState))
                    : onPaginationChange(updater);
                },
                enableTopToolbar: true,
                // TODO: this should be ListLearningBudgetTransactionsTable concern, using onColumnFilterChange
                renderTopToolbar: ({ table }) =>
                  !table.getRowCount() && !showClearButton ? (
                    <div className="empty:hidden" />
                  ) : (
                    <Stack
                      flexDirection="row"
                      className="px-2 py-4 pt-3 gap-12 items-baseline"
                    >
                      <TableFilter
                        className="shrink-0"
                        id="date"
                        label="Sort by"
                        value={
                          search.dateInterval
                            ? search.dateInterval
                            : search.initialDate || search.finalDate
                              ? 'custom_date'
                              : ''
                        }
                        onChange={e => {
                          if (e.target.value === 'custom_date') {
                            e.preventDefault();
                            e.stopPropagation();
                            if (datePickerModalOpen) {
                              return;
                            }
                            setDatePickerModalOpen(true);
                            return;
                          }

                          if (!e.target.value) {
                            navigate({
                              search: prev => ({
                                ...prev,
                                dateInterval: undefined,
                                initialDate: undefined,
                                finalDate: undefined,
                              }),
                            });
                            return;
                          }

                          navigate({
                            search: prev => ({
                              ...prev,
                              initialDate: undefined,
                              finalDate: undefined,
                              dateInterval:
                                (e.target.value as CustomDateInterval) ||
                                undefined,
                            }),
                          });
                        }}
                        renderValue={selected => {
                          return formatConstant(selected) || 'All';
                        }}
                      >
                        <MenuItem value="">All</MenuItem>
                        {Object.values(CustomDateInterval).map(interval => (
                          <MenuItem
                            key={interval}
                            value={interval}
                            className="capitalize"
                          >
                            {formatConstant(interval)}
                          </MenuItem>
                        ))}
                        <MenuItem
                          value="custom_date"
                          onClickCapture={() => {
                            setDatePickerModalOpen(true);
                          }}
                        >
                          Pick a Date
                        </MenuItem>
                      </TableFilter>
                      <TableFilter
                        className="shrink-0"
                        label="Status"
                        id="status-select"
                        value={
                          Array.isArray(search.status)
                            ? search.status
                            : search.status
                              ? [search.status]
                              : []
                        }
                        multiple
                        onChange={e => {
                          navigate({
                            search: prev => ({
                              ...prev,
                              status: e.target
                                .value as LearningBudgetTransactionStatus[],
                            }),
                          });
                        }}
                        displayEmpty
                        renderValue={selected => {
                          if (!selected.length) {
                            return 'All';
                          }
                          if (selected.length > 1) {
                            return (
                              <div className="flex gap-2 items-center capitalize break-all text-ellipsis">
                                {selected[0]} and {selected.length - 1} more
                              </div>
                            );
                          }

                          return selected.map(formatConstant).join(', ');
                        }}
                      >
                        {statusFilterOptions.map(option => (
                          <MenuItem
                            key={option.value}
                            value={option.value}
                            classes={{
                              root: 'p-2',
                            }}
                          >
                            <div className="flex gap-2 items-center capitalize">
                              <Checkbox
                                sx={{
                                  padding: 0,
                                  '&.Mui-checked': {
                                    color: 'primary',
                                  },
                                }}
                                checked={
                                  Array.isArray(search.status)
                                    ? search.status.includes(option.value)
                                    : search.status === option.value
                                }
                              />
                              <ListItemText>{option.label}</ListItemText>
                            </div>
                          </MenuItem>
                        ))}
                      </TableFilter>

                      <SearchableMultiSelect
                        className="shrink-0"
                        id="team"
                        label="Team"
                        isLoading={isLoadingSelectedTeams}
                        value={
                          Array.isArray(search.teamId)
                            ? search.teamId
                            : search.teamId
                              ? [search.teamId]
                              : []
                        }
                        options={
                          availableTeams?.map(team => ({
                            id: team._id,
                            label: team.name,
                          })) ?? []
                        }
                        onSearch={setTeamName}
                        searchPlaceholder="Search by team"
                        onChange={value => {
                          navigate({
                            search: prev => ({
                              ...prev,
                              teamId: value,
                            }),
                          });
                        }}
                      />

                      <TableFilter
                        className="shrink-0"
                        label="Labels"
                        id="labels-select"
                        value={
                          Array.isArray(search.labels)
                            ? search.labels
                            : search.labels
                              ? [search.labels]
                              : []
                        }
                        multiple
                        onChange={e => {
                          navigate({
                            search: prev => ({
                              ...prev,
                              labels: e.target.value as string[],
                            }),
                          });
                        }}
                        displayEmpty
                        renderValue={selected => {
                          if (!selected.length) {
                            return 'All';
                          }
                          if (selected.length > 1) {
                            return (
                              <div className="flex gap-2 items-center capitalize break-all text-ellipsis">
                                {selected[0]} and {selected.length - 1} more
                              </div>
                            );
                          }

                          return selected.map(formatConstant).join(', ');
                        }}
                      >
                        {Object.values(LearningBudgetTransactionLabel).map(
                          option => (
                            <MenuItem
                              key={option}
                              value={option}
                              classes={{
                                root: 'p-2',
                              }}
                            >
                              <div className="flex gap-2 items-center capitalize">
                                <Checkbox
                                  sx={{
                                    padding: 0,
                                    '&.Mui-checked': {
                                      color: 'primary',
                                    },
                                  }}
                                  checked={
                                    Array.isArray(search.labels)
                                      ? search.labels.includes(option)
                                      : search.labels === option
                                  }
                                />
                                <ListItemText>
                                  {formatConstant(option)}
                                </ListItemText>
                              </div>
                            </MenuItem>
                          ),
                        )}
                      </TableFilter>
                      {showClearButton && (
                        <Button
                          onClick={() => {
                            if (searchInputRef.current) {
                              searchInputRef.current.value = '';
                            }
                            navigate({ search: {} });
                          }}
                          color="secondary"
                          variant="text"
                          className="shrink-0"
                        >
                          Clear
                        </Button>
                      )}
                      <div className="flex gap-10 ml-auto">
                        <Button
                          startIcon={<Download />}
                          variant="text"
                          color="secondary"
                          loading={isLoadingExport}
                          onClick={onDownloadClick}
                          className="shrink-0"
                        >
                          Download list
                        </Button>

                        <TextField
                          defaultValue={search.userName ?? ''}
                          size="small"
                          placeholder="Search by user"
                          inputRef={searchInputRef}
                          slotProps={{
                            input: {
                              endAdornment: <Search />,
                            },
                          }}
                          onChange={debounce(e => {
                            navigate({
                              search: prev => ({
                                ...prev,
                                userName: e.target.value || undefined,
                              }),
                            });
                          }, 300)}
                        />
                      </div>
                    </Stack>
                  ),
              }}
            />
          </div>
          <DatePickerModal
            open={datePickerModalOpen}
            initialDate={search.initialDate}
            finalDate={search.finalDate}
            onClose={values => {
              setDatePickerModalOpen(false);

              if (!values) {
                return;
              }

              const { initialDate, finalDate } = values;

              navigate({
                search: {
                  ...search,
                  dateInterval: undefined,
                  initialDate: initialDate.toISOString(),
                  finalDate: finalDate.toISOString(),
                },
              });
            }}
          />
        </TabPanel>
      </TabContext>
    </div>
  );
}
